import { Controller } from "@hotwired/stimulus";

// Adapted from https://github.com/excid3/tailwindcss-stimulus-components/blob/master/src/tabs.js
export default class extends Controller {
  static classes = ["activeTab", "inactiveTab"];
  static targets = ["tab", "panel", "select"];
  static values = {
    index: -1,
  };

  // Turbo Drive only reacts to popstate for entries carrying its own state
  // object (History#onPopState checks event.state.turbo). The entries pushed
  // below deliberately lack it, so we restore the tab from the URL ourselves.
  syncFromUrl = () => {
    // A pop to another page is Turbo's business; it swaps the DOM right after.
    if (window.location.pathname !== this.pagePath) return;

    this.applyAnchor();
  };

  connect() {
    this.pagePath = window.location.pathname;

    // No history entry on load, otherwise every page view leaves a phantom
    // "?tab=<default>" entry behind and the back button appears dead.
    this.applyAnchor();

    window.addEventListener("popstate", this.syncFromUrl);
  }

  disconnect() {
    window.removeEventListener("popstate", this.syncFromUrl);
  }

  // Changes to the clicked tab
  change(event) {
    if (event.currentTarget.tagName === "SELECT") {
      this.indexValue = event.currentTarget.selectedIndex;

      // If target specifies an index, use that
    } else if (event.currentTarget.dataset.index) {
      this.indexValue = event.currentTarget.dataset.index;

      // If target specifies an id, use that
    } else if (event.currentTarget.dataset.id) {
      this.indexValue = this.tabTargets.findIndex(
        (tab) => tab.dataset.id == event.currentTarget.dataset.id,
      );

      // Otherwise, use the index of the current target
    } else {
      this.indexValue = this.tabTargets.indexOf(event.currentTarget);
    }

    this.pushTabToUrl();

    window.dispatchEvent(new CustomEvent("tsc:tab-change"));
  }

  nextTab() {
    this.indexValue = Math.min(this.indexValue + 1, this.tabsCount - 1);
    this.pushTabToUrl();
  }

  previousTab() {
    this.indexValue = Math.max(this.indexValue - 1, 0);
    this.pushTabToUrl();
  }

  firstTab() {
    this.indexValue = 0;
    this.pushTabToUrl();
  }

  lastTab() {
    this.indexValue = this.tabsCount - 1;
    this.pushTabToUrl();
  }

  // Selects the tab named in the URL without touching the history.
  applyAnchor() {
    // A missing or unknown ?tab= yields -1, which falls back to the first tab.
    const index = this.tabTargets.findIndex(
      (tab) => tab.dataset.id === this.anchor,
    );

    this.indexValue = Math.max(index, 0);

    // indexValueChanged is driven by a MutationObserver, so it neither runs
    // synchronously nor runs at all when the value did not change. Render here
    // to keep the panels in sync with the URL either way.
    this.showTab();
  }

  // Only user driven tab changes add a history entry. Doing this from
  // indexValueChanged would also fire on connect, which used to leave a phantom
  // "?tab=<default>" entry on every page view and made the back button look dead.
  pushTabToUrl() {
    if (this.indexValue < 0) return;

    const newTab = this.tabTargets[this.indexValue]?.dataset.id;

    if (!newTab) return;

    const url = new URL(window.location);

    if (url.searchParams.get("tab") === newTab) return;

    url.searchParams.set("tab", newTab);

    history.pushState({}, "", url.href);
  }

  indexValueChanged() {
    if (this.indexValue < 0) return;

    this.showTab();
  }

  showTab() {
    this.panelTargets.forEach((panel, index) => {
      const tab = this.tabTargets[index];

      if (index === this.indexValue) {
        panel.classList.remove("hidden");
        if (this.hasInactiveTabClass)
          tab?.classList?.remove(...this.inactiveTabClasses);
        if (this.hasActiveTabClass)
          tab?.classList?.add(...this.activeTabClasses);
      } else {
        panel.classList.add("hidden");
        if (this.hasActiveTabClass)
          tab?.classList?.remove(...this.activeTabClasses);
        if (this.hasInactiveTabClass)
          tab?.classList?.add(...this.inactiveTabClasses);
      }
    });

    if (this.hasSelectTarget) {
      this.selectTarget.selectedIndex = this.indexValue;
    }
  }

  get tabsCount() {
    return this.tabTargets.length;
  }

  get anchor() {
    const urlParams = new URLSearchParams(window.location.search);

    return urlParams.get("tab");
  }
}
